import React, { useEffect, useState } from 'react'
import { Form } from '@s4l/ds'
import { IconTrash } from '@s4l/icons/IconTrash'
import { useS4LContext } from '@s4l/react-context'
import { withErrorBoundary } from '@s4l/utils/hocs/withErrorBoundary'
import PriceHistogram from '../../PriceHistogram'
import styles from './index.module.css'
import { ListOfPriceDistribution } from '@s4l/stayforlong/property/ListOfPriceDistribution'
import { PriceDistribution } from '@s4l/stayforlong/property/PriceDistribution'
import { useUseCase } from '@s4l/utils/hooks/useUseCase'
import { Money } from '@s4l/stayforlong/kernel/Money'

interface IFilterPricingProps {
  defaultMinPrice?: number
  defaultMaxPrice?: number
  isLoading?: boolean
  listOfPriceDistributionJSON: Parameters<(typeof ListOfPriceDistribution)['fromJSON']>[0]
}

const FilterPricing = ({
  isLoading,
  listOfPriceDistributionJSON,
  defaultMinPrice,
  defaultMaxPrice,
}: IFilterPricingProps) => {
  const { i18n } = useS4LContext()

  const [applyDefaultValues, setApplyDefaultValues] = useState(false)
  const { data: priceType, loading: isLoadingPriceType } = useUseCase('GetPriceTypeSharedUseCase')
  const { data: currency, loading: isLoadingCurrency } = useUseCase('GetCurrencySharedUseCase')
  const { data: market, loading: isLoadingMarket } = useUseCase('GetCurrentMarketSharedUseCase')

  const listOfPriceDistributionModel = ListOfPriceDistribution.fromJSON(listOfPriceDistributionJSON)
  const min = listOfPriceDistributionModel.getMinValue()
  const max = listOfPriceDistributionModel.getMaxValue()

  const isEmpty = listOfPriceDistributionModel.isEmpty()

  const [selectedRange, setSelectedRange] = useState<number[]>([])

  const [minValue, setMinValue] = useState<number | string>()
  const [maxValue, setMaxValue] = useState<number | string>()

  const handleChange = (value: number | number[]) => {
    if (Array.isArray(value)) {
      setSelectedRange(value)

      const minPrice =
        value[0] === 0
          ? ''
          : (listOfPriceDistributionModel
              .getList()
              .at(value[0] - 1)
              ?.getValue() ?? '')

      const maxPrice =
        value[1] === listOfPriceDistributionModel.getList().length + 1
          ? ''
          : (listOfPriceDistributionModel
              .getList()
              .at(value[1] - 1)
              ?.getValue() ?? '')

      setMinValue(minPrice)
      setMaxValue(maxPrice)
    }
  }

  const handleInputChange = (name: 'min' | 'max', value: number | string) => {
    const numValue = Number(value || (name === 'min' ? 0 : Number.MAX_SAFE_INTEGER))

    if (Number.isNaN(numValue)) {
      return
    }

    let listOfPriceDistributionModelList = listOfPriceDistributionModel.getList()
    listOfPriceDistributionModelList = [
      PriceDistribution.create({ value: 0, count: 0 }),
      ...listOfPriceDistributionModelList,
      PriceDistribution.create({ value: Number.MAX_SAFE_INTEGER, count: 0 }),
    ]

    let closestIndex = listOfPriceDistributionModelList.reduce((prevIndex, curr, currIndex) => {
      const prevValue = listOfPriceDistributionModelList[prevIndex].getValue()
      return Math.abs(curr.getValue() - numValue) < Math.abs(prevValue - numValue)
        ? currIndex
        : prevIndex
    }, 0)

    // Adjust for min empty value
    if (name === 'min' && value === '') {
      closestIndex = 0
    }

    // Adjust for max boundary
    if (name === 'max' && value === max) {
      closestIndex = listOfPriceDistributionModel.getList().length - 1
    }

    setSelectedRange(prev => {
      if (name === 'min') {
        return [closestIndex, prev[1]]
      }

      return [prev[0], closestIndex]
    })

    if (name === 'min') {
      setMinValue(value)
    } else {
      setMaxValue(value)
    }
  }

  useEffect(() => {
    if (!isEmpty) {
      setSelectedRange([0, listOfPriceDistributionModel.getList().length + 1])
      setMinValue(defaultMinPrice ?? '')
      setMaxValue(defaultMaxPrice ?? '')
      handleInputChange('min', defaultMinPrice ?? '')
      handleInputChange('max', defaultMaxPrice ?? '')
      setApplyDefaultValues(true)
    }
  }, [isEmpty]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (listOfPriceDistributionModel.getList().length === 0 || !applyDefaultValues) return

    const newSelectedRange = [...selectedRange]

    const newMinValue =
      newSelectedRange[0] > 0
        ? listOfPriceDistributionModel.getClosestByValue(Number(minValue ?? 0), {
            get: 'floor',
          })
        : null

    const newMaxValue = listOfPriceDistributionModel.getClosestByValue(
      maxValue === '' ? Number.MAX_SAFE_INTEGER : Number(maxValue),
      {
        get: 'ceil',
      }
    )

    if (
      !newSelectedRange[0] ||
      !newMinValue ||
      (newMinValue && newMinValue.getValue() > Number(minValue)) ||
      !minValue
    ) {
      newSelectedRange[0] = 0
    } else {
      newSelectedRange[0] = listOfPriceDistributionModel.findIndexByValue(newMinValue.getValue())
    }

    if (!newSelectedRange[1] || !newMaxValue || !maxValue) {
      newSelectedRange[1] = listOfPriceDistributionModel.getList().length + 1
    } else {
      newSelectedRange[1] =
        listOfPriceDistributionModel.findIndexByValue(newMaxValue.getValue()) + 1
    }

    setSelectedRange(newSelectedRange)
  }, [listOfPriceDistributionModel.serialized]) // eslint-disable-line react-hooks/exhaustive-deps

  const modelMinValue =
    selectedRange[0] === 0
      ? 0
      : (listOfPriceDistributionModel
          .getList()
          .at(selectedRange[0] - 1)
          ?.getValue() ?? 0)

  const modelMaxValue =
    listOfPriceDistributionModel
      .getList()
      .at(selectedRange[1] - 1)
      ?.getValue() ?? max + 1

  const hasLoaded = !isLoadingPriceType && !isLoadingCurrency && !isLoadingMarket

  let histogramData = ListOfPriceDistribution.create({
    list: [
      ...listOfPriceDistributionModel.getList(),
      ...(listOfPriceDistributionModel.getList().length > 0
        ? [
            PriceDistribution.create({
              value: (listOfPriceDistributionModel.getMax()?.getValue() ?? 0) + 1,
              count: listOfPriceDistributionModel.getMax()?.getCount() ?? 0,
            }),
          ]
        : []),
    ],
  })

  histogramData = histogramData.setSelectedRange([modelMinValue, modelMaxValue])

  const currencySymbol = Money.fromJSON({
    amount: 0,
    currency: currency?.value ?? 'USD',
  })?.getSymbol(i18n.locale, market)

  const priceLabel = priceType?.isFeaturedPriceTotal()
    ? i18n.formatMessage({ id: 'global.total-price' })
    : i18n.formatMessage({ id: 'global.price-per-night' })

  const titleMessageKey = hasLoaded ? `${priceLabel} (${currencySymbol})` : priceLabel

  return (
    <Form.Fieldset data-testid={hasLoaded ? 'loaded' : 'not-loaded'}>
      <Form.Legend>{titleMessageKey}</Form.Legend>
      <Form.ClearButton icon={<IconTrash />}>
        {i18n.formatMessage({ id: 'serp.filter-clear' })}
      </Form.ClearButton>

      <div className={styles.gridContainer}>
        <PriceHistogram
          className={['col-start-2', 'col-span-2']}
          listOfPriceDistributionJSON={histogramData.toJSON()}
        />

        <div className="col-start-1 col-span-4">
          <Form.Skeleton isLoading={isLoading}>
            <Form.Slider
              name={['minIndex', 'maxIndex']}
              min={0}
              max={listOfPriceDistributionModel.getList().length + 1}
              step={1}
              value={selectedRange}
              onChange={handleChange}
              disabled={isEmpty}
            />
          </Form.Skeleton>
        </div>

        <Form.Label className={['mt-32', 'desktop:mt-24', 'col-span-2']}>
          {i18n.formatMessage({ id: 'global.minimum' })}
          <Form.Input
            name="minPrice"
            min={min}
            max={max}
            falseValue=""
            value={minValue}
            type="tel"
            onBlur={value => handleInputChange('min', value)}
            onClear={() => handleInputChange('min', '')}
            displayValue={value => {
              if (value === '') {
                return '0'
              }
              return value
            }}
            parseValue={value => {
              if (!value) {
                return ''
              }

              const numValue = Number(value)
              return Number.isNaN(numValue) ? '' : numValue
            }}
            onChange={setMinValue}
            disabled={isEmpty}
          />
        </Form.Label>
        <Form.Label className={['mt-32', 'desktop:mt-24', 'col-span-2']}>
          {i18n.formatMessage({ id: 'global.maximum' })}
          <Form.Input
            name="maxPrice"
            min={min}
            max={max}
            falseValue=""
            value={maxValue}
            type="tel"
            onBlur={value => handleInputChange('max', value)}
            onClear={() => handleInputChange('max', '')}
            displayValue={value => {
              if (value === '') {
                return `+${max}`
              }
              return value
            }}
            parseValue={value => {
              if (!value) {
                return ''
              }

              const numValue = Number(value)
              return Number.isNaN(numValue) ? '' : numValue
            }}
            onChange={setMaxValue}
            disabled={isEmpty}
          />
        </Form.Label>
      </div>
    </Form.Fieldset>
  )
}

FilterPricing.displayName = 'SearchFilters.FilterPricing'
export default withErrorBoundary(FilterPricing)
